import { useQuery } from '@tanstack/react-query';
import { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { userInfoQueryOptions } from '../iam/userInfoQuery';
import { contentQueryOptions } from '../content/contentQuery';
import { configQueryOptions } from '../basic/configQuery';
import { KpaDatalayerObject } from '../content/types/global';
import { navigationQueryOptions } from '../ui/Navigation/navigationQuery';
import { findNavigationItemInMenu } from '../ui/Navigation/findNavigationItemInMenu';
import { isOneTrustLoadedAtom } from './tagManagementStore';
import { useAtomValue } from 'jotai';
import { pathToLocationKey, toPathKey } from '../basic/locationKey';
import { ContentTypeGuid } from '../content/types/enums';
import OneTrustCookieIdCategories from './OneTrustCookieIdCategories';

const Adobe: FC = (): ReactNode => {
  const trackedLocation = useRef<string | undefined>(undefined);
  const location = useLocation();
  const isOneTrustLoaded = useAtomValue(isOneTrustLoadedAtom);
  const userInfo = useQuery(userInfoQueryOptions);
  const content = useQuery(contentQueryOptions(pathToLocationKey(location)));
  const config = useQuery(configQueryOptions);
  const navigation = useQuery(navigationQueryOptions);
  const adobeTagId = 'adobeTagId';

  const updateDatalayer = useCallback(() => {
    if (!content.isSuccess || !userInfo.isSuccess || !navigation.isSuccess)
      return new Error('Vi har redan kollat att de är success...');

    const updatedDatalayer: KpaDatalayerObject = {
      pageUrl: document.location.protocol + '//' + document.location.hostname + (document.location.port ? ':' + document.location.port : '') + location.pathname,
      website: 'arbetsgivare.kpa.se',
    };

    const siteName = 'Entrésidan';
    updatedDatalayer[`siteSection1`] = siteName;
    let finalSiteSection: number = 2;
    let currentSection = siteName;
    const itemInMenu = findNavigationItemInMenu(navigation.data, toPathKey(location.pathname));
    itemInMenu?.breadcrumbs.forEach((crumb, i) => {
      if (i === 0)
        return;
      currentSection += `|${crumb.name}`;
      updatedDatalayer[`siteSection${(i + 1)}`] = currentSection;
      finalSiteSection = i + 2;
    });
    updatedDatalayer.pageName = currentSection + '|' + content.data?.name;
    updatedDatalayer[`siteSection${finalSiteSection}`] = updatedDatalayer.pageName;

    const pageTypeMap: Partial<Record<ContentTypeGuid, string>> = {
      [ContentTypeGuid.Standardsida]: 'Standardsida',
      [ContentTypeGuid.Samlingssida]: 'Samlingssida',
      [ContentTypeGuid.Nyhet]: 'Nyhet',
      [ContentTypeGuid.Nyhetsarkiv]: 'Nyhetsarkiv',
      [ContentTypeGuid.Kalenderhändelse]: 'Händelse',
      [ContentTypeGuid.Kalenderhändelsearkiv]: 'Kalenderhändelsearkiv',
    };

    if (content.data?.contentTypeGuid && pageTypeMap[content.data?.contentTypeGuid]) {
      updatedDatalayer.pageType = pageTypeMap[content.data?.contentTypeGuid];
    }

    if (userInfo.data?.användarnamn) {
      updatedDatalayer.user_id = userInfo.data?.användarnamn;
    }
    if (userInfo.data?.arbetsgivarebehörigheter.length) {
      // Väljer användarens "första bästa" arbetsgivare
      updatedDatalayer.employer_id = userInfo.data?.arbetsgivarebehörigheter[0].namn;
    }

    window.datalayer = updatedDatalayer;
  }, [
    content.isSuccess,
    content.data?.name,
    content.data?.contentTypeGuid,
    userInfo.isSuccess,
    userInfo.data?.användarnamn,
    userInfo.data?.arbetsgivarebehörigheter,
    navigation.isSuccess,
    navigation.data,
    location,
  ]);

  useEffect(() => {
    if (!content.isSuccess || !userInfo.isSuccess || !config.isSuccess || !navigation.isSuccess)
      return;
    if (!isOneTrustLoaded)
      return;
    if (location.key === trackedLocation.current)
      return;

    trackedLocation.current = location.key;

    updateDatalayer();

    ensureAdobeLoadsAndTrackPageView(adobeTagId, config.data.adobeLaunchScriptSrc);
  }, [
    location,
    content.isSuccess,
    content.data,
    userInfo.isSuccess,
    userInfo.data,
    config.isSuccess,
    config.data,
    navigation.isSuccess,
    navigation.data,
    isOneTrustLoaded,
    updateDatalayer,
  ]);

  return <></>;
};

interface AdobeOptInService {
  // https://experienceleague.adobe.com/en/docs/id-service/using/implementation/opt-in-service/api
  approve: (categories: string[], shouldWaitForComplete: boolean) => unknown;
  complete: () => void;
};

interface AdobeObject {
  optIn: AdobeOptInService;
};

declare global {
  interface Window {
    adobe?: AdobeObject;
  }
};

function ensureAdobeLoadsAndTrackPageView(adobeTagId: string, adobeLaunchScriptSrc: string) {
  if (document.getElementById(adobeTagId) === null) {
    const scriptElement = document.createElement('script');
    scriptElement.id = adobeTagId;
    scriptElement.src = adobeLaunchScriptSrc;
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.onload = () => {
      setAdobeOptIn();
    };
    window.OptanonWrapper = () => {
      setAdobeOptIn();
    };
    // 20250403 Rule "Page view" (i KPAs Adobe Launch) som skickar track-beacon till Adobe Analytics triggras automatiskt 1 gång när Adobe Launch-skriptet laddas in:
    document.head.appendChild(scriptElement);
  }
  else if (window._satellite && window.__satelliteLoaded) {
    // 20250403 Detta är trigger för Rule "Virtual Page view" i KPAs Adobe Launch som skickar track-beacon till Adobe Analytics:
    window._satellite.track('virtualPageView');
  }
  else {
    console.error('Should have tracked virtual pageview now but Adobe script was not loaded');
  }
}

function setAdobeOptIn() {
  if (!window.adobe || !window.adobe.optIn || !window.OptanonActiveGroups)
    return;

  // Mapping table from OneTrust Cookie ID categories (OnetrustActiveGroups) to Adobe Experience Cloud ID (ECID) Opt-in service Opt-in categories
  const consentMapping: { [key: string]: string } = {
    aa: `,${OneTrustCookieIdCategories.Statistiska},`,
    ecid: `,${OneTrustCookieIdCategories.Statistiska},`,
  };

  const consents = [];
  for (const key in consentMapping) {
    if (window.OptanonActiveGroups.includes(consentMapping[key])) {
      consents.push(key);
    }
  }
  if (consents.length > 0) {
    window.adobe.optIn.approve(consents, true);
  }
  window.adobe.optIn.complete();
}

export default Adobe;
